// FAZ-9 / MOD-18 (REQ-51) — LDAP/AD yapılandırma API client (security.manage).
//
// Uçlar `/api/v1/admin/ldap/*`. `bindSecret` write-only: GET'te `"***"` maskeli
// döner (alan boş bırakılır, okunmaz), PUT'ta `null`/boş gönderilince BE mevcut
// sırrı korur (`hasBindSecret` bir sır kayıtlı mı bilgisini taşır). `useSsl` hep
// true (salt-görünüm). test-connection body'siz; `{success, error}` döner
// (error sabit-kategori — ham hata sızmaz).

import { http, unwrap, type ActionResponse } from './http';
import { LDAP_DEFAULT_PORT } from '@features/ldap/constants';
import type { LdapConfig, LdapConfigInput, LdapTestResult } from '@features/ldap/types';

interface LdapConfigDtoWire {
  id?: string | null;
  host?: string | null;
  port?: number | null;
  useSsl?: boolean | null;
  bindDn?: string | null;
  /** GET'te `"***"` maskesiyle gelir — asla okunmaz/prefill edilmez. */
  bindSecret?: string | null;
  hasBindSecret?: boolean | null;
  baseDn?: string | null;
  userSearchFilter?: string | null;
  groupBaseDn?: string | null;
  groupRoleMapJson?: string | null;
  trustedCertThumbprint?: string | null;
  enabled?: boolean | null;
  updatedAt?: string | null;
}

interface LdapTestResultDtoWire {
  success?: boolean | null;
  error?: string | null;
}

const BASE = '/api/v1/admin/ldap';

function mapConfig(d: LdapConfigDtoWire): LdapConfig {
  return {
    host: d.host ?? '',
    port: d.port ?? LDAP_DEFAULT_PORT,
    useSsl: d.useSsl ?? true,
    bindDn: d.bindDn ?? '',
    hasBindSecret: d.hasBindSecret ?? false,
    baseDn: d.baseDn ?? '',
    userSearchFilter: d.userSearchFilter ?? '',
    groupBaseDn: d.groupBaseDn ?? '',
    groupRoleMapJson: d.groupRoleMapJson ?? '',
    trustedCertThumbprint: d.trustedCertThumbprint ?? '',
    enabled: d.enabled ?? false,
    updatedAt: d.updatedAt ?? null,
  };
}

export const ldapApi = {
  async getConfig(): Promise<LdapConfig> {
    const res = await http.get<ActionResponse<LdapConfigDtoWire>>(`${BASE}/config`);
    return mapConfig(unwrap(res));
  },

  async updateConfig(input: LdapConfigInput): Promise<LdapConfig> {
    // `useSsl` gönderilmez — BE her zaman true zorlar (BIZ-LDAP-02).
    const res = await http.put<ActionResponse<LdapConfigDtoWire>>(`${BASE}/config`, {
      host: input.host,
      port: input.port,
      bindDn: input.bindDn,
      // Boş bırakıldıysa null gönder — BE mevcut sırrı korur.
      bindSecret: input.bindSecret,
      baseDn: input.baseDn,
      userSearchFilter: input.userSearchFilter,
      groupBaseDn: input.groupBaseDn,
      groupRoleMapJson: input.groupRoleMapJson,
      trustedCertThumbprint: input.trustedCertThumbprint,
      enabled: input.enabled,
    });
    return mapConfig(unwrap(res));
  },

  async testConnection(): Promise<LdapTestResult> {
    const res = await http.post<ActionResponse<LdapTestResultDtoWire>>(`${BASE}/test-connection`);
    const dto = unwrap(res);
    return { success: dto.success ?? false, error: dto.error ?? null };
  },
};
